// src/views/StoreAnalytics.js
import React, { useState, useEffect } from "react";
import {
  CCard,
  CCardBody,
  CCardHeader,
  CCol,
  CRow,
  CWidgetStatsA,
  CTable,
  CTableHead,
  CTableRow,
  CTableHeaderCell,
  CTableBody,
  CTableDataCell,
  CBadge,
  CFormSelect,
  CSpinner,
  CFormLabel,
} from "@coreui/react";
import { CChartBar, CChartDoughnut, CChartLine } from "@coreui/react-chartjs";
import CIcon from "@coreui/icons-react";
import { cibEventStore, cilChartPie, cilMap, cilCalendar } from "@coreui/icons";
import { getSummary, getGeographicDistribution, getSalesPerformance, getMapDistribution, getVisibilityTrends, getStockPerStoreMonthly } from "src/services/Analytics";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import "leaflet/dist/leaflet.css";
import { useNavigate } from "react-router-dom";
import SalesPicker from "src/components/SalesPicker";
import ls from "local-storage";
import StockLatest from "src/components/StockLatest";

// Fix for default marker icons (required for React-Leaflet)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.7.1/dist/images/marker-shadow.png",
});
const StoreAnalytics = () => {
  const [summary, setSummary] = useState([]);
  // const [visitTrends, setVisitTrends] = useState([]);
  const [visibilityTrends, setVisibilityTrends] = useState([]);
  const [mapDistribution, setMapDistribution] = useState([]);
  const [geoDistribution, setGeoDistribution] = useState([]);
  const [salesPerformance, setSalesPerformance] = useState([]);
  const [timeRange, setTimeRange] = useState("monthly");
  const [loading, setLoading] = useState(true);
  const [stockMonthly, setStockMonthly] = useState(null);
  const [stockRatioGroup, setStockRatioGroup] = useState("below_50");
  const [datasetPage, setDatasetPage] = useState(0);
  const datasetsPerPage = 10; // Show 5 datasets per page, adjust as needed

  const navigate = useNavigate();

  const center = [-6.91, 107.61]; // Latitude, Longitude of Bandung

  const getFilterParams = () => {
    const smd = ls.get("smd");
    const prov = ls.get("prov");
    const city = ls.get("city");
    
    return {
      smdId: smd?.id && smd.id !== "999" && smd.value !== "all" ? smd.id : "0",
      provinceId: prov?.id && prov.id !== "999" && prov.value !== "all" ? prov.id : "0",
      cityId: city?.id && city.id !== "999" && city.value !== "all" ? city.id : "0",
    };
  };

  const handleBarClick = (index) => {
    if (index == null) return;
    if (index < geoDistribution.length) {
      const id = geoDistribution[index].id;
      navigate(`/stores?provinceId=${id}`)
    }
  };

  const fetchData = React.useCallback(async () => {
    const filters = getFilterParams();
    setLoading(true);
    try {
      const [resx, maps, geo, sales] = await Promise.all([
        getSummary(filters),
        getMapDistribution(filters),
        getGeographicDistribution(filters), // Sekarang menerima parameter smdId, provinceId, cityId
        getSalesPerformance(filters), // Sekarang menerima parameter smdId, provinceId, cityId
      ]);
      setSummary(resx);
      setMapDistribution(maps.stores);
      setGeoDistribution(geo.data);
      setSalesPerformance(sales.data);
      
      // visibility trend fetch should not block page
      try {
        const vis = await getVisibilityTrends(timeRange, filters);
        setVisibilityTrends(vis?.data || vis || []);
      } catch (e) {
        setVisibilityTrends([]);
      }
      
      // stock per store monthly should not block page
      try {
        const monthly = await getStockPerStoreMonthly({ ...filters, months: 6 });
        setStockMonthly(monthly?.data || monthly || null);
      } catch (e) {
        setStockMonthly([]);
      }
    } catch (error) {
      console.error("Error fetching analytics data:", error);
    } finally {
      setLoading(false);
    }
  }, [timeRange]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  function buildVisibilityTrendDataset(data) {
    // Get all unique periods (month, quarter, etc.)
    const labels = [...new Set(data.map(item => item.label))];

    // Get all unique visibility levels
    const visibilities = [...new Set(data.map(item => item.visibility))];

    // Define a color palette (expand as needed)
    const colorPalette = [
      "#FF6384", // red
      "#36A2EB", // blue
      "#FFCE56", // yellow
      "#4BC0C0", // teal
      "#9966FF", // purple
      "#FF9F40", // orange
      "#00A86B", // green
      "#C71585", // violet
    ];

    // Build datasets for each visibility
    const datasets = visibilities.map((visibility, idx) => ({
      label: visibility,
      data: labels.map(label => {
        const found = data.find(item =>
          (item.month === label || item.quarter === label || item.yearweek === label || item.biweek === label) &&
          item.visibility === visibility
        );
        return found ? found.visit_count : 0;
      }),
      borderColor: colorPalette[idx % colorPalette.length],
      backgroundColor: colorPalette[idx % colorPalette.length] + "80", // add transparency for fill
      fill: false,
      tension: 0.4, // smooth lines
      pointBackgroundColor: colorPalette[idx % colorPalette.length],
      pointBorderColor: "#fff",
      pointRadius: 5,
      pointHoverRadius: 7,
      borderWidth: 3,
    }));

    return { labels, datasets };
  }

  const buildMonthlyChart = (raw, groupKey = "above_75") => {
    if (!raw || !raw[groupKey] || !Array.isArray(raw[groupKey]) || !raw[groupKey].length) {
      return { labels: [], datasets: [] };
    }
    const arr = raw[groupKey];
    const months = [...new Set(arr.map(r => r.month))];
    const storeNames = [...new Set(arr.map(r => r.store_name))];
    const datasets = storeNames.map((store, idx) => ({
      label: store,
      data: months.map(m => {
        const found = arr.find(r => r.month === m && r.store_name === store);
        return found ? found.products_with_stock : 0;
      }),
      borderColor: ["#4BC0C0", "#36A2EB", "#FFCE56", "#FF6384", "#9966FF", "#FF9F40"][idx % 6],
      backgroundColor: "transparent",
      tension: 0.3,
    }));
    return { labels: months, datasets };
  };

  const chart = buildMonthlyChart(stockMonthly, stockRatioGroup);

  // Calculate total pages based on datasets length
  const totalPages = Math.ceil(chart.datasets.length / datasetsPerPage);

  // Reset page if datasets change and current page is out of range
  useEffect(() => {
    if (datasetPage > totalPages - 1) setDatasetPage(0);
  }, [chart.datasets, totalPages]);

  // Slice datasets for the current page
  const paginatedDatasets = chart.datasets.slice(
    datasetPage * datasetsPerPage,
    (datasetPage + 1) * datasetsPerPage
  );

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ height: "80vh" }}>
        <CSpinner color="primary" size="lg" />
      </div>
    );
  }

  // Add click handlers for doughnut charts
  const handleTypeDistributionClick = (event, elements) => {
    if (elements.length > 0) {
      const dataIndex = elements[0].index;
      const label = summary.data.distribution[dataIndex]?.type;
      navigate(`/stores?type=${label}`)
      // You can add additional logic here like navigation or showing details
    }
  };

  const handleVisibilityDistributionClick = (event, elements) => {
    if (elements.length > 0) {
      const dataIndex = elements[0].index;
      const label = summary.data.visibility[dataIndex]?.visibility;
      navigate(`/stores?visibility=${label}`)
      // You can add additional logic here like navigation or showing details
    }
  };

  return (
    <div className="store-analytics">
      <SalesPicker onFilterChange={fetchData} />
      <StockLatest />
      <CRow>
        <CCol xs={12} md={6} lg={3}>
          <CWidgetStatsA
            className="mb-4"
            color="primary"
            value={summary.data.summary.totalStores}
            title="Total Toko"
            icon={<CIcon icon={cibEventStore} height={36} />}
          />
        </CCol>
        <CCol xs={12} md={6} lg={3}>
          <CWidgetStatsA
            className="mb-4"
            color="info"
            value={
              summary.data.distribution
                .filter((s) => s.type === "A")
                .map((s) => s.count) || 0
            }
            title="Toko Tipe A"
            icon={<CIcon icon={cilCalendar} height={36} />}
          />
        </CCol>
        <CCol xs={12} md={6} lg={3}>
          <CWidgetStatsA
            className="mb-4"
            color="warning"
            value={summary.data.summary.visitedStores}
            title="Toko Terkunjungi"
            icon={<CIcon icon={cilMap} height={36} />}
          />
        </CCol>
        <CCol xs={12} md={6} lg={3}>
          <CWidgetStatsA
            className="mb-4"
            color="danger"
            value={summary.data.summary.notVisitedStores}
            title="Toko Belum Terkunjungi"
            icon={<CIcon icon={cilChartPie} height={36} />}
          />
        </CCol>
      </CRow>
      <CRow>
        <CCol lg={12}>
          <CCard className="mb-4">
            <CCardHeader>
              <CRow className="align-items-center">
                <CCol>Stock per Store (Monthly)</CCol>
                <CCol className="text-end">
                  <small className="text-muted">Last 6 months</small>
                  <CFormSelect
                    size="sm"
                    style={{
                      width: "auto",
                      display: "inline-block",
                      marginLeft: 12,
                    }}
                    value={stockRatioGroup}
                    onChange={(e) => setStockRatioGroup(e.target.value)}
                  >
                    <option value="below_50">Stock Ratio &lt; 50%</option>
                    <option value="between_50_75">Stock Ratio 50-75%</option>
                    <option value="above_75">Stock Ratio &gt; 75%</option>
                  </CFormSelect>
                </CCol>
              </CRow>
            </CCardHeader>
            <CCardBody>
              {paginatedDatasets.length ? (
                <>
                  <CChartLine
                    data={{ labels: chart.labels, datasets: paginatedDatasets }}
                    options={{
                      responsive: true,
                      plugins: { legend: { position: "bottom" } },
                      interaction: { mode: "index", intersect: false },
                      scales: {
                        y: { beginAtZero: true, ticks: { precision: 0 } },
                      },
                    }}
                    style={{ minHeight: 300 }}
                  />
                  <div className="d-flex justify-content-center mt-2">
                    <button
                      className="btn btn-sm btn-secondary"
                      disabled={datasetPage === 0}
                      onClick={() => setDatasetPage(datasetPage - 1)}
                    >
                      Previous
                    </button>
                    <span className="mx-2">
                      Page {datasetPage + 1} of {totalPages}
                    </span>
                    <button
                      className="btn btn-sm btn-secondary"
                      disabled={datasetPage >= totalPages - 1}
                      onClick={() => setDatasetPage(datasetPage + 1)}
                    >
                      Next
                    </button>
                  </div>
                </>
              ) : (
                <div className="text-muted">Tidak ada data stok bulanan</div>
              )}
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>

      <CRow>
        {/* Visibility Trend */}
        <CCol lg={12}>
          <CCard className="mb-4">
            <CCardHeader>
              <CRow className="align-items-center">
                <CCol>Tren Visibility</CCol>
                <CCol className="text-end">
                  <CFormSelect
                    size="sm"
                    style={{ width: "auto", display: "inline-block" }}
                    value={timeRange}
                    onChange={(e) => setTimeRange(e.target.value)}
                  >
                    <option value="weekly">7 Hari</option>
                    <option value="biweekly">14 Hari</option>
                    <option value="monthly">30 Hari</option>
                    <option value="quarterly">90 Hari</option>
                  </CFormSelect>
                </CCol>
              </CRow>
            </CCardHeader>
            <CCardBody>
              {(() => {
                const chart = buildVisibilityTrendDataset(visibilityTrends);
                return chart.labels.length ? (
                  <CChartLine
                    data={{ labels: chart.labels, datasets: chart.datasets }}
                    options={{
                      responsive: true,
                      plugins: { legend: { position: "bottom" } },
                      interaction: { mode: "index", intersect: false },
                      scales: {
                        y: { beginAtZero: true, ticks: { precision: 0 } },
                      },
                    }}
                    style={{ minHeight: 260 }}
                  />
                ) : (
                  <div className="text-muted">Tidak ada data tren</div>
                );
              })()}
            </CCardBody>
          </CCard>
        </CCol>
        <CCol lg={6}>
          <CCard className="mb-4">
            <CCardHeader>
              <CRow>
                <CCol>Distribusi toko berdasarkan tipe</CCol>
              </CRow>
            </CCardHeader>
            <CCardBody>
              <CChartDoughnut
                data={{
                  labels: summary.data.distribution.map(
                    (item) => `Type ${item.type}`
                  ),
                  datasets: [
                    {
                      backgroundColor: [
                        "#4BC0C0",
                        "#36A2EB",
                        "#FFCE56",
                        "#FF6384",
                      ],
                      data: summary.data.distribution.map((item) => item.count),
                    },
                  ],
                }}
                options={{
                  onClick: handleTypeDistributionClick,
                }}
              />
            </CCardBody>
          </CCard>
        </CCol>

        <CCol lg={6}>
          <CCard className="mb-4">
            <CCardHeader>
              <CRow>
                <CCol>Distribusi toko berdasarkan visibility</CCol>
              </CRow>
            </CCardHeader>
            <CCardBody>
              <CChartDoughnut
                data={{
                  labels: summary.data.visibility.map(
                    (item) => ` ${item.visibility}`
                  ),
                  datasets: [
                    {
                      backgroundColor: ["#4BC0C0", "#36A2EB", "#FFCE56"],
                      data: summary.data.visibility.map(
                        (item) => item.store_count
                      ),
                    },
                  ],
                }}
                options={{
                  onClick: handleVisibilityDistributionClick,
                }}
              />
              <CFormLabel>*Berdasarkan data kunjungan terakhir</CFormLabel>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
      <CRow>
        <CCol lg={12}>
          <CCard className="mb-4">
            <CCardHeader>Sebaran Toko Berdasarkan Lokasi</CCardHeader>
            <CCardBody>
              <MapContainer
                center={center}
                zoom={13}
                style={{ height: "600px", width: "100%" }}
              >
                <TileLayer
                  url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                  attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                />

                {mapDistribution.map((store) => (
                  <Marker
                    key={store.id}
                    position={
                      isNaN(store.latitude)
                        ? center
                        : [store.latitude, store.longitude]
                    }
                  >
                    <Popup>
                      <div
                        style={{ cursor: "pointer" }}
                        onClick={() => navigate(`/stores/details/${store.id}`)}
                      >
                        <h3>{store.name}</h3>
                        <p>{store.address}</p>
                        <p
                          style={{
                            color: "#0066cc",
                            textDecoration: "underline",
                            marginTop: "8px",
                          }}
                        >
                          Lihat Detail →
                        </p>
                      </div>
                    </Popup>
                  </Marker>
                ))}
              </MapContainer>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
      <CRow>
        <CCol lg={6}>
          <CCard className="mb-4">
            <CCardHeader>Geographic Distribution</CCardHeader>
            <CCardBody>
              <CChartBar
                data={{
                  labels: geoDistribution.map((item) => item.province),
                  datasets: [
                    {
                      label: "Stores per Province",
                      backgroundColor: "#f87979",
                      data: geoDistribution.map((item) => item.store_count),
                    },
                  ],
                }}
                options={{
                  indexAxis: "y",
                  responsive: true,
                  plugins: {
                    legend: {
                      position: "right",
                    },
                  },
                  onClick: (evt, elements) => {
                    if (elements && elements.length > 0) {
                      const idx = elements[0].index;
                      handleBarClick(idx);
                    }
                  },
                }}
              />
            </CCardBody>
          </CCard>
        </CCol>

        <CCol lg={6}>
          <CCard>
            <CCardHeader>Stores Needing Attention</CCardHeader>
            <CCardBody style={{ maxHeight: "400px", overflowY: "auto" }}>
              <CTable striped hover responsive>
                <CTableHead>
                  <CTableRow>
                    <CTableHeaderCell>Store</CTableHeaderCell>
                    <CTableHeaderCell>Type</CTableHeaderCell>
                    <CTableHeaderCell>Last Visit</CTableHeaderCell>
                    <CTableHeaderCell>Days Since Visit</CTableHeaderCell>
                  </CTableRow>
                </CTableHead>
                <CTableBody>
                  {salesPerformance.map((store) => (
                    <CTableRow
                      key={store.id}
                      onClick={() => navigate(`/stores/details/${store.id}`)}
                    >
                      <CTableDataCell>{store.store_name}</CTableDataCell>
                      <CTableDataCell>
                        <CBadge
                          color={store.type === "A" ? "primary" : "secondary"}
                        >
                          Type {store.type}
                        </CBadge>
                      </CTableDataCell>
                      <CTableDataCell>
                        {store.last_visited
                          ? new Date(store.last_visited).toLocaleDateString()
                          : "Never"}
                      </CTableDataCell>
                      <CTableDataCell>
                        <CBadge
                          color={
                            store.days_since_last_visit > 30
                              ? "danger"
                              : "warning"
                          }
                        >
                          {store.days_since_last_visit || "N/A"}
                        </CBadge>
                      </CTableDataCell>
                    </CTableRow>
                  ))}
                </CTableBody>
              </CTable>
            </CCardBody>
          </CCard>
        </CCol>
      </CRow>
    </div>
  );
};

export default StoreAnalytics;
